/**
 * Shared phase content — single source of truth for MoodMap modals, Dashboard card, and cycleCalculations.
 */

export const PHASE_CONTENT = {
  Menstrual: {
    emoji: "\u{1F9F8}",
    name: "Menstrual",
    days: "1\u20135",
    punchline: "Recovery Week: She\u2019s running on empty.",
    cardTagline: "Low fuel. Keep it calm.",
    planningTip: "Maybe not the day for big plans \u2014 a cozy night in is a better call.",
    playByPlay: "Hormones bottom out, triggering the shedding of the uterine lining. Her body is literally doing a full reset, which dips energy, slows momentum, and increases sensitivity. This is her recovery week \u2014 the physiology equivalent of playing through a tough away game.",
    briefPlayByPlay: "Hormones bottom out \u2014 her body\u2019s hitting reset.",
    briefFeelings: "Low energy, cravings, wants comfort and calm.",
    feelsPhysical: [
      "Cramps, bloating, fatigue",
      "Back aches or pelvic pressure",
      "Cravings",
    ],
    feelsEmotional: [
      "Low energy, inward-focused",
      "Receptive to calmness and the right type of humor",
      "OK with space",
    ],
    prep: [
      "\u201CFine\u201D could mean anything. Don\u2019t argue, don\u2019t smother. She\u2019ll talk when she\u2019s ready.",
      "Comfort is king. She\u2019ll appreciate warmth, quiet, snacks, and her \u201Cme-time.\u201D",
      "Clutter and messiness can be especially triggering right now.",
      "Savory, salty, and chocolatey \u2014 your allies this week. Take inventory.",
    ],
    action: [
      "Be supportive and comforting, even if it\u2019s from a distance.",
      "Dirty dishes in the sink? It\u2019s your time to shine!",
      "Grab the heating pad and activate your massage hands for instant hero status.",
      "She needs space? You need gains. Hit the gym, come back grounded.",
    ],
  },
  Follicular: {
    emoji: "\u{1F337}",
    name: "Follicular",
    days: "6\u201313",
    punchline: "Preseason hype! She\u2019s warming up.",
    cardTagline: "Warming up, ready to move.",
    planningTip: "Good day to plan almost anything \u2014 she\u2019s open and up for it.",
    playByPlay: "Estrogen starts climbing again after her period, rebuilding energy, mood, and motivation. Her body is gearing up for ovulation \u2014 think of it as the warm-up phase before game day. Hormones boost clarity, optimism, and creativity. She\u2019s getting back in the pocket.",
    briefPlayByPlay: "Estrogen\u2019s climbing \u2014 she\u2019s warming up and gaining momentum.",
    briefFeelings: "More energy, sharper focus, up for anything.",
    feelsPhysical: [
      "Energy returning, better sleep",
      "Less aches and pains",
      "Appetite steady and predictable",
    ],
    feelsEmotional: [
      "Sharper thinking and more clarity \u2014 flow state energy",
      "Optimistic and future-oriented",
      "More social, confident, and adventurous",
    ],
    prep: [
      "Audible! Get ready to break the routine, and don\u2019t be afraid to take some (calculated) risks.",
      "Game clock\u2019s in your favor \u2014 she\u2019s in her \u201Cyes-to-plans\u201D zone. If you\u2019ve got a big idea, this is the week.",
      "It\u2019s also the best time for a difficult conversation.",
      "Compliments land hard right now; she\u2019ll notice you noticing.",
    ],
    action: [
      "Pick one solid plan \u2014 dinner, a show, a day trip \u2014 and lock it in. Don\u2019t overthink it; just set the play and send the invite.",
      "Switch up the usual pattern \u2014 try a new spot, a new activity, or a small adventure. Lead with confidence and keep it light.",
      "Run the play you\u2019ve been dodging. Pick a quiet moment, open the huddle, and talk it out.",
      "Drop a clean, targeted compliment. No fluff. No paragraphs. Just a sharp \u201CI noticed that\u201D \u2014 bull\u2019s eye!",
      "Got gym gains from last week? Go ahead and showboat; you\u2019ve got an audience.",
    ],
  },
  Ovulation: {
    emoji: "\u{1F525}",
    name: "Ovulation",
    days: "14\u201316",
    punchline: "Prime Time: MVP energy!",
    cardTagline: "Prime time. MVP energy.",
    planningTip: "Good day for a date night or something special \u2014 she\u2019ll be present for it.",
    playByPlay: "Estrogen peaks and she hits her physiological high-performance window. The egg is released, hormones boost energy, confidence, and social drive. This is her most naturally connected, outward-facing stretch of the cycle.",
    briefPlayByPlay: "Estrogen peaks \u2014 she\u2019s in her high-performance window.",
    briefFeelings: "Max confidence, more social, more affectionate.",
    feelsPhysical: [
      "Aches or pinching sensations on one side",
      "Higher energy, feeling in the zone",
      "Clearer skin, brighter eyes",
    ],
    feelsEmotional: [
      "Maximum confidence",
      "More social and outgoing",
      "More communicative and affectionate",
    ],
    prep: [
      "She\u2019s in peak-performance mode \u2014 energized, confident, and tuned in. This week is worth prioritizing.",
      "Her connection dial is turned up \u2014 chemistry feels easier and more natural right now.",
      "Compliments land hard \u2014 she actually feels them this week.",
      "Playoff mode \u2014 she\u2019s social, present, and fully engaged. Great window for quality time.",
    ],
    action: [
      "Clear a little space in the schedule and make one standout plan \u2014 treat this week like prime time.",
      "Lean into closeness: initiate time together, be warm, be engaged. Confidence leads the play.",
      "Drop a sharp, specific compliment. Keep it clean and sincere \u2014 it\u2019ll land like a highlight reel.",
      "Be fully present: phone down, attention up. Bring your A-game in effort and attitude.",
    ],
  },
  "Early Luteal": {
    emoji: "\u{1F3E0}",
    name: "Luteal",
    days: "17\u201323",
    punchline: "Wind-down mode: Nesting and grounded.",
    cardTagline: "Winding down. Stay cozy.",
    planningTip: "Good day for a low-key night in or a home project \u2014 skip anything high-stimulation.",
    playByPlay: "Progesterone rises after ovulation, shifting her body into wind-down mode. Hormones slow things down, increase sensitivity, and push her toward routine, comfort, and stability.",
    briefPlayByPlay: "Progesterone rises \u2014 her body\u2019s shifting into wind-down mode.",
    briefFeelings: "Lower energy, craving routine and comfort.",
    feelsPhysical: [
      "Bloating and tenderness",
      "Cravings and sensitive digestion",
      "Lower energy",
    ],
    feelsEmotional: [
      "Ready to wind down, less social, more introspective",
      "Nesting mode, ready to get cozy, nurturing",
      "Prefers routine and predictability",
    ],
    prep: [
      "Stage 5 Clinger? Embrace it.",
      "Not all heroes wear capes. Keep it low-key \u2014 cozy nights beat wild adventures right now.",
      "Shifting into nesting mode means it\u2019s a great time to get things done around the house.",
      "Gratitude and energy-matching will be greatly appreciated this week.",
    ],
    action: [
      "Tackle a home task without complaining \u2014 it matches her nesting rhythm and shows presence.",
      "Plan something low-key: a cozy night, a simple meal, a calm vibe. Keep it predictable.",
      "Notice the extra things she\u2019s doing around the house and say a real thank-you.",
      "Ask for her take on a decision \u2014 she\u2019s in a grounded, nurturing headspace.",
    ],
  },
  "Late Luteal/PMS": {
    emoji: "\u26A0\uFE0F",
    name: "PMS",
    days: "24\u201328",
    punchline: "High-alert week! Sensitivity\u2019s up.",
    cardTagline: "Sensitivity\u2019s up. Play smart.",
    planningTip: "Might not be the day for big decisions or plans that can\u2019t flex \u2014 keep it easy to cancel.",
    playByPlay: "Estrogen and progesterone dive faster than a soccer player in the penalty box \u2014 this is the hormonal cliff that creates PMS. This is NOT. HER. MOOD. It\u2019s physiology reacting to a rapid chemical shift.",
    briefPlayByPlay: "Estrogen and progesterone crash \u2014 that\u2019s PMS, not her mood.",
    briefFeelings: "Sensitive, short-fused, running low on bandwidth.",
    feelsPhysical: [
      "Bloating, cramping, headaches, cravings, you name it",
      "If it sucks, she\u2019s probably feeling it",
    ],
    feelsEmotional: [
      "Sensitive and short-fused",
      "Easily overwhelmed and fatigued",
      "Brain fog; try doing math with your head chopped off and you\u2019ve got the idea",
    ],
    prep: [
      "Patience and bandwidth for her are lower across the board. Be ready to compensate.",
      "Crying at a dog food commercial? Totally normal \u2014 a hug goes a long way.",
      "Don\u2019t be caught off guard lacking essentials; take inventory (comfort foods).",
      "Flexibility wins this week. Be open to forfeiting or postponing plans.",
    ],
    action: [
      "Not in the mood to cook? Order in \u2014 no shame in delivery.",
      "Squash the snarky PMS/period/time-of-month remarks. She knows what\u2019s up; it won\u2019t end well.",
      "Tension brewing over something small? Sideline it for later when things are calmer.",
      "Pantry running low on her favorites? Stock up like the apocalypse is coming.",
    ],
  },
};

// Date-seeded random picker: stable for the day, changes at midnight
export function dateSeedPick(arr, salt = '') {
  if (!arr || arr.length === 0) return '';
  const today = new Date();
  const seed = today.getFullYear() * 10000 + (today.getMonth() + 1) * 100 + today.getDate();
  let hash = seed;
  for (let i = 0; i < salt.length; i++) hash = ((hash << 5) - hash + salt.charCodeAt(i)) | 0;
  return arr[Math.abs(hash) % arr.length];
}
